/**
 * Serviço de gerenciamento de questionários (form-builder).
 *
 * Responsabilidades:
 * - IO / integração FHIR (listar e arquivar questionários via status retired).
 * - Evitar que componentes/hook façam parsing FHIR diretamente.
 *
 * Importante:
 * - A listagem é feita via busca por `identifier` (FHIR token search),
 *   usando o mesmo `system` e padrão de `value` do transformer do construtor.
 */

import { fhirClients } from "src-next/shared/api/fhirClient";

import type { QuestionnairePayload } from "../../form-builder/types";

import { toPayloadFromFhirQuestionnaire } from "../../form-builder/utils/questionnaireQuestionnaireTransformers";
import { QUESTIONNAIRE_ORGANIZATION_IDENTIFIER_SYSTEM } from "../../form-builder/utils/questionnaireQuestionnaireConstants";
import { useFhirData } from "@/utils/useFhirData";

function getQuestionnaireClient() {
  return fhirClients.admin.resource<fhir5.Questionnaire>("Questionnaire");
}

function buildIdentifierToken(params: { system: string; value: string }) {
  // Em FHIR, busca por token `identifier` usa o formato `system|value`.
  return `${params.system}|${params.value}`;
}

/**
 * Lista questionários do `organizationId` atual via busca por `identifier`.
 *
 * Padrão do `value`:
 * - `Organization/{organizationId}`
 *
 * O transformer do construtor persiste `identifier[0]` com:
 * - `system`: QUESTIONNAIRE_ORGANIZATION_IDENTIFIER_SYSTEM
 * - `value`: `Organization/{organizationId}`
 */
async function listByOrganizationIdentifier(
  organizationId: string,
): Promise<QuestionnairePayload[]> {
  const client = getQuestionnaireClient();
  const organizationReference = `Organization/${useFhirData.ensureOrganizationIdWithOidPrefix(organizationId)}`;

  const identifierToken = buildIdentifierToken({
    system: QUESTIONNAIRE_ORGANIZATION_IDENTIFIER_SYSTEM,
    value: organizationReference,
  });

  const bundle = await client.search({
    identifier: identifierToken,
    _count: "100",
  });

  const entries = bundle.entry ?? [];

  const payloads = entries
    .map((entry) => entry.resource)
    .filter(
      (resource): resource is fhir5.Questionnaire =>
        resource !== undefined &&
        resource.resourceType === "Questionnaire" &&
        // Soft delete: não exibir questionários retirados na UI de gerenciar.
        resource.status !== "retired",
    )
    .map((resource) => toPayloadFromFhirQuestionnaire(resource))
    .filter((payload): payload is QuestionnairePayload => payload !== null);

  // Mantém ordem previsível para UX consistente.
  return payloads.sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Arquivamento lógico (soft delete): lê o recurso completo, define apenas
 * `status: "retired"` (FHIR) e persiste com PUT — sem DELETE (HAPI admin).
 *
 * A API permanece `removeById` para o hook/UI (“Excluir”); semanticamente é retirar da gestão.
 */
async function removeById(questionnaireId: string): Promise<void> {
  const client = getQuestionnaireClient();
  const current = await client.read(questionnaireId);
  await client.update(questionnaireId, {
    ...current,
    resourceType: "Questionnaire",
    status: "retired",
  });
}

export const questionnaireManagementService = {
  listByOrganizationIdentifier,
  removeById,
};
